/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, MouseEventHandler } from "react"
import { Checkbox, Row, Col } from "@duxton-web/ui"
import { CheckboxChangeEvent } from "@duxton-web/ui/lib/checkbox"
import { EditableFilled } from "@duxton-web/icons"
import { useDispatch } from "react-redux"
import { actionCreator } from "store/store"
import { SubDepartment } from "types/models/menu"

// Opens the item-selection drawer on checkbox click unless the overview list
// is already in selection mode. Shared by every overview row checkbox.
export const useItemSelectionDrawerClick = (showItemSelection?: boolean) => {
  const dispatch = useDispatch()
  const handleClickSelected: MouseEventHandler<HTMLElement> = e => {
    e.stopPropagation()
    if (!showItemSelection) {
      dispatch(
        actionCreator.menu.toggleDrawer({
          showItemSelection: true,
        }),
      )
    }
  }
  return handleClickSelected
}

interface IMenuRowCheckboxProps {
  showItemSelection?: boolean
  disableCheckbox: boolean
  checked: boolean
  indeterminate: boolean
  onCheck: (value: boolean) => void
}

// Selection checkbox shared by MenuDepartmentRow and MenuDepartmentRowV3.
export const MenuRowCheckbox: FC<IMenuRowCheckboxProps> = ({
  showItemSelection,
  disableCheckbox,
  checked,
  indeterminate,
  onCheck,
}: IMenuRowCheckboxProps) => {
  const handleClickSelected = useItemSelectionDrawerClick(showItemSelection)

  return (
    <Checkbox
      className={`checkbox-container ${showItemSelection ? "show" : ""}`}
      disabled={disableCheckbox}
      indeterminate={indeterminate}
      defaultChecked={false}
      checked={checked}
      onChange={(e: CheckboxChangeEvent) => onCheck(e.target.checked)}
      onClick={handleClickSelected}
    />
  )
}

interface IMenuSubDepartmentRowActionsProps {
  subDepartment: SubDepartment
  showItemSelection?: boolean
  disableCheckbox: boolean
  checked: boolean
  indeterminate: boolean
  onCheck: (value: boolean) => void
}

// Edit and selection controls shared by MenuSubDepartmentRow and
// MenuSubDepartmentRowV3.
export const MenuSubDepartmentRowActions: FC<IMenuSubDepartmentRowActionsProps> = ({
  subDepartment,
  showItemSelection,
  disableCheckbox,
  checked,
  indeterminate,
  onCheck,
}: IMenuSubDepartmentRowActionsProps) => {
  const dispatch = useDispatch()

  const handleClickEdit = () => {
    dispatch(
      actionCreator.menu.toggleDrawer({
        subDepartment,
        showSubDepartment: true,
      }),
    )
  }

  return (
    <Row
      className={`show-hover-container ${showItemSelection ? "show" : ""}`}
      wrap={false}
    >
      <Col className={`editBtn ${showItemSelection ? "" : "show"}`}>
        <EditableFilled onClick={handleClickEdit} />
      </Col>
      <Col>
        <MenuRowCheckbox
          showItemSelection={showItemSelection}
          disableCheckbox={disableCheckbox}
          checked={checked}
          indeterminate={indeterminate}
          onCheck={onCheck}
        />
      </Col>
    </Row>
  )
}
